package com.mgacreative.hotel.feature.reports.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.shared.ui.designsystem.components.HFLoadingState
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun DashboardScreen(onNavigate: (String) -> Unit = {}) {
    val viewModel = koinViewModel<DashboardViewModel>()
    val state by viewModel.state.collectAsState()
    val scrollState = rememberScrollState()

    LaunchedEffect(Unit) {
        viewModel.loadDashboardData()
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFFF8FAFC))
    ) {
        if (state.isLoading) {
            HFLoadingState()
        } else {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.TopCenter
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .widthIn(max = 1400.dp)
                        .verticalScroll(scrollState)
                        .verticalScrollbar(scrollState)
                        .padding(horizontal = 20.dp, vertical = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(20.dp)
                ) {
                    // 1. Executive Welcome & Header Bar
                    ExecutiveHeader(
                        occupancyPercent = state.occupancyPercent,
                        onRefresh = { viewModel.loadDashboardData() }
                    )

                    // 2. High-Priority Quick Actions Bar
                    QuickActionsRow(onNavigate)

                    // 3. Core Operational Metrics Grid
                    MetricsGrid(state, onNavigate)

                    // 4. Analytics & Occupancy Trend Chart
                    OccupancyChartSection(state)

                    // 5. Quick Navigation Module Hub
                    ModuleShortcutsSection(onNavigate)

                    Spacer(modifier = Modifier.height(24.dp))
                }
            }
        }
    }
}

@Composable
fun ExecutiveHeader(
    occupancyPercent: Int,
    onRefresh: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 18.dp, vertical = 16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Left: Title and subtitle
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(44.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .background(Color(0xFFEFF6FF)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        Icons.Default.Dashboard,
                        contentDescription = null,
                        tint = Color(0xFF2563EB),
                        modifier = Modifier.size(24.dp)
                    )
                }
                Spacer(modifier = Modifier.width(14.dp))
                Column {
                    Text(
                        text = "HotelOS Yönetim Paneli",
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = Color(0xFF0F172A)
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = "Otel operasyonları ve anlık doluluk durumu",
                        fontSize = 12.sp,
                        color = Color(0xFF64748B)
                    )
                }
            }

            // Right: Live Occupancy Badge & Refresh Button
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                val occColor = when {
                    occupancyPercent >= 80 -> Color(0xFFDC2626)
                    occupancyPercent >= 50 -> Color(0xFFD97706)
                    else -> Color(0xFF16A34A)
                }
                val occBg = when {
                    occupancyPercent >= 80 -> Color(0xFFFEE2E2)
                    occupancyPercent >= 50 -> Color(0xFFFEF3C7)
                    else -> Color(0xFFDCFCE7)
                }

                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(20.dp))
                        .background(occBg)
                        .padding(horizontal = 14.dp, vertical = 6.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(occColor)
                        )
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(
                            text = "Doluluk: %$occupancyPercent",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Bold,
                            color = occColor
                        )
                    }
                }

                OutlinedButton(
                    onClick = onRefresh,
                    shape = RoundedCornerShape(10.dp),
                    contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                    modifier = Modifier.height(36.dp)
                ) {
                    Icon(Icons.Default.Refresh, contentDescription = "Yenile", tint = Color(0xFF2563EB), modifier = Modifier.size(16.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("Yenile", fontSize = 12.sp, color = Color(0xFF2563EB), fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
fun QuickActionsRow(onNavigate: (String) -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        QuickActionButton(
            title = l10n("New Reservation"),
            subtitle = "Rezervasyon Oluştur",
            icon = Icons.Default.AddCircleOutline,
            accentColor = Color(0xFF059669),
            bgColor = Color(0xFFECFDF5),
            modifier = Modifier.weight(1f),
            onClick = { onNavigate("CreateReservationScreen") }
        )
        QuickActionButton(
            title = l10n("Quick Check-in"),
            subtitle = "Giriş İşlemleri",
            icon = Icons.AutoMirrored.Filled.ArrowForward,
            accentColor = Color(0xFF2563EB),
            bgColor = Color(0xFFEFF6FF),
            modifier = Modifier.weight(1f),
            onClick = { onNavigate("CheckInListScreen") }
        )
        QuickActionButton(
            title = l10n("Quick Check-out"),
            subtitle = "Çıkış & Folio",
            icon = Icons.AutoMirrored.Filled.ArrowBack,
            accentColor = Color(0xFFDC2626),
            bgColor = Color(0xFFFEF2F2),
            modifier = Modifier.weight(1f),
            onClick = { onNavigate("CheckOutListScreen") }
        )
        QuickActionButton(
            title = "Oda Durumları",
            subtitle = "Canlı Oda Rack",
            icon = Icons.Default.MeetingRoom,
            accentColor = Color(0xFF7C3AED),
            bgColor = Color(0xFFF5F3FF),
            modifier = Modifier.weight(1f),
            onClick = { onNavigate("RoomStatusScreen") }
        )
    }
}

@Composable
fun QuickActionButton(
    title: String,
    subtitle: String,
    icon: ImageVector,
    accentColor: Color,
    bgColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(14.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = modifier.clickable { onClick() }
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(42.dp)
                    .clip(RoundedCornerShape(10.dp))
                    .background(bgColor),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = title, tint = accentColor, modifier = Modifier.size(22.dp))
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = title,
                    fontWeight = FontWeight.Bold,
                    fontSize = 13.sp,
                    color = Color(0xFF0F172A),
                    maxLines = 1
                )
                Text(
                    text = subtitle,
                    fontSize = 11.sp,
                    color = Color(0xFF64748B),
                    maxLines = 1
                )
            }
        }
    }
}

@Composable
fun MetricsGrid(state: DashboardState, onNavigate: (String) -> Unit) {
    var showAgencyAllotments by remember { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        // Row 1: Arrivals & Departures
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            MetricCard(
                title = l10n("Expected Arrivals"),
                subtitle = "Bugün Beklenen Giriş",
                value = state.todaysCheckIns.toString(),
                icon = Icons.Default.PersonAdd,
                color = Color(0xFF2563EB),
                bgColor = Color(0xFFEFF6FF),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("CheckInListScreen") }
            )
            MetricCard(
                title = l10n("Expected Departures"),
                subtitle = "Bugün Beklenen Çıkış",
                value = state.todaysCheckOuts.toString(),
                icon = Icons.Default.ExitToApp,
                color = Color(0xFFDC2626),
                bgColor = Color(0xFFFEF2F2),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("CheckOutListScreen") }
            )
        }

        // Row 2: Available Rooms & Maintenance
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            MetricCard(
                title = l10n("Available Rooms"),
                subtitle = "Satışa Hazır / Müsait",
                value = state.availableRooms.toString(),
                icon = Icons.Default.CheckCircle,
                color = Color(0xFF16A34A),
                bgColor = Color(0xFFF0FDF4),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("NewReservationCalendarScreen") }
            )
            MetricCard(
                title = l10n("Cleaning / Maintenance"),
                subtitle = "Kirli / Bakımda Olanlar",
                value = state.roomsInMaintenance.toString(),
                icon = Icons.Default.CleaningServices,
                color = Color(0xFF7C3AED),
                bgColor = Color(0xFFF5F3FF),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("HousekeepingScreen") }
            )
        }

        // Row 3: Occupied Rooms & Agency Allotment
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            MetricCard(
                title = l10n("Occupied Rooms"),
                subtitle = "Şu An Dolu / Konaklamada",
                value = state.occupiedRooms.toString(),
                icon = Icons.Default.MeetingRoom,
                color = Color(0xFFE11D48),
                bgColor = Color(0xFFFFF1F2),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("InUseRoomsScreen") }
            )
            MetricCard(
                title = l10n("Agency Allotment"),
                subtitle = "Acenta Tahsisleri",
                value = state.totalAgencyAllotments.toString(),
                icon = Icons.Default.Assignment,
                color = Color(0xFFD97706),
                bgColor = Color(0xFFFFFBEB),
                modifier = Modifier.weight(1f),
                onClick = { showAgencyAllotments = true }
            )
        }
    }

    // Agency Allotment Details Dialog
    if (showAgencyAllotments) {
        AlertDialog(
            onDismissRequest = { showAgencyAllotments = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(16.dp),
            title = {
                Text(
                    text = l10n("Agency Allotments (Today)"),
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A)
                )
            },
            text = {
                if (state.agencyAllotmentDetails.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = l10n("No allotments reserved for today."),
                            color = Color(0xFF64748B),
                            fontSize = 14.sp
                        )
                    }
                } else {
                    LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        items(items = state.agencyAllotmentDetails) { allotment ->
                            Surface(
                                shape = RoundedCornerShape(10.dp),
                                color = Color(0xFFF8FAFC),
                                border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(12.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column {
                                        Text(
                                            text = allotment.agencies?.name ?: l10n("Unknown Agency"),
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 14.sp,
                                            color = Color(0xFF0F172A)
                                        )
                                        Text(
                                            text = "Kontenjan Blokajı",
                                            fontSize = 11.sp,
                                            color = Color(0xFF64748B)
                                        )
                                    }
                                    Box(
                                        modifier = Modifier
                                            .clip(RoundedCornerShape(6.dp))
                                            .background(Color(0xFFFEF3C7))
                                            .padding(horizontal = 10.dp, vertical = 4.dp)
                                    ) {
                                        Text(
                                            text = "${allotment.allottedRooms} " + l10n("Rooms"),
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 12.sp,
                                            color = Color(0xFFD97706)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = { showAgencyAllotments = false },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(l10n("Close"), fontWeight = FontWeight.Bold)
                }
            }
        )
    }
}

@Composable
fun MetricCard(
    title: String,
    subtitle: String,
    value: String,
    icon: ImageVector,
    color: Color,
    bgColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = modifier.clickable { onClick() }
    ) {
        Column(
            modifier = Modifier.padding(18.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(bgColor),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(icon, contentDescription = title, tint = color, modifier = Modifier.size(22.dp))
                }

                Text(
                    text = value,
                    fontSize = 26.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = color
                )
            }

            Spacer(modifier = Modifier.height(14.dp))

            Text(
                text = title,
                fontSize = 14.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF0F172A)
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = subtitle,
                fontSize = 11.sp,
                color = Color(0xFF64748B)
            )
        }
    }
}

@Composable
fun OccupancyChartSection(state: DashboardState) {
    val pagerState = rememberPagerState(pageCount = { 3 })
    val coroutineScope = rememberCoroutineScope()
    
    val titles = listOf(
        l10n("Haftalık Doluluk Oranı"), 
        l10n("Aylık Doluluk Oranı"), 
        l10n("Yıllık Doluluk Oranı")
    )
    
    val weeklyData = if (state.weeklyData.isEmpty()) listOf(0f) else state.weeklyData
    val weeklyLabels = if (state.weeklyLabels.isEmpty()) listOf("") else state.weeklyLabels
    
    val monthlyData = if (state.monthlyData.isEmpty()) listOf(0f) else state.monthlyData
    val monthlyLabels = if (state.monthlyLabels.isEmpty()) listOf("") else state.monthlyLabels
    
    val yearlyData = if (state.yearlyData.isEmpty()) listOf(0f) else state.yearlyData
    val yearlyLabels = if (state.yearlyLabels.isEmpty()) listOf("") else state.yearlyLabels

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            // Chart Header with Period Selector
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(32.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFFEFF6FF)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Default.ShowChart, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(18.dp))
                    }
                    Spacer(modifier = Modifier.width(10.dp))
                    Column {
                        Text(
                            text = titles[pagerState.currentPage],
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            text = "Dönemsel doluluk trend analizi",
                            fontSize = 11.sp,
                            color = Color(0xFF64748B)
                        )
                    }
                }

                // Period Pill Toggle Tabs
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(10.dp))
                        .background(Color(0xFFF1F5F9))
                        .padding(3.dp),
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    listOf("Hafta", "Ay", "Yıl").forEachIndexed { index, label ->
                        val isSelected = pagerState.currentPage == index
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(8.dp))
                                .background(if (isSelected) Color.White else Color.Transparent)
                                .clickable {
                                    coroutineScope.launch { pagerState.animateScrollToPage(index) }
                                }
                                .padding(horizontal = 12.dp, vertical = 6.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = label,
                                fontSize = 12.sp,
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                color = if (isSelected) Color(0xFF2563EB) else Color(0xFF64748B)
                            )
                        }
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(20.dp))
            
            HorizontalPager(
                state = pagerState,
                modifier = Modifier.fillMaxWidth().height(210.dp)
            ) { page ->
                when (page) {
                    0 -> SplineAreaChart(data = weeklyData, labels = weeklyLabels, modifier = Modifier.fillMaxSize())
                    1 -> SplineAreaChart(data = monthlyData, labels = monthlyLabels, modifier = Modifier.fillMaxSize())
                    2 -> SplineAreaChart(data = yearlyData, labels = yearlyLabels, modifier = Modifier.fillMaxSize())
                }
            }
        }
    }
}

@Composable
fun SplineAreaChart(data: List<Float>, labels: List<String>, modifier: Modifier = Modifier) {
    val maxData = 100f
    val gradientColors = listOf(Color(0xFF2563EB).copy(alpha = 0.25f), Color(0xFF3B82F6).copy(alpha = 0.01f))
    val lineColor = Color(0xFF2563EB)
    val gridColor = Color(0xFFF1F5F9)
    val textMeasurer = rememberTextMeasurer()

    Canvas(modifier = modifier.padding(horizontal = 8.dp, vertical = 4.dp)) {
        val width = size.width
        val totalHeight = size.height
        val graphHeight = totalHeight - 24.dp.toPx()
        val stepX = width / (data.size - 1).coerceAtLeast(1)

        // Draw light horizontal grid lines
        for (i in 0..3) {
            val y = graphHeight * (i / 3f)
            drawLine(
                color = gridColor,
                start = androidx.compose.ui.geometry.Offset(0f, y),
                end = androidx.compose.ui.geometry.Offset(width, y),
                strokeWidth = 1.dp.toPx()
            )
        }

        val path = Path()
        var lastX = 0f
        var lastY = graphHeight - (data[0] / maxData) * graphHeight

        path.moveTo(lastX, lastY)
        val points = mutableListOf(Pair(lastX, lastY))

        for (i in 1 until data.size) {
            val currentX = i * stepX
            val currentY = graphHeight - (data[i] / maxData) * graphHeight
            
            val controlX1 = lastX + (currentX - lastX) / 2
            val controlX2 = lastX + (currentX - lastX) / 2

            path.cubicTo(
                controlX1, lastY,
                controlX2, currentY,
                currentX, currentY
            )

            points.add(Pair(currentX, currentY))
            lastX = currentX
            lastY = currentY
        }

        // Draw fill gradient
        val fillPath = Path().apply {
            addPath(path)
            lineTo(width, graphHeight)
            lineTo(0f, graphHeight)
            close()
        }

        drawPath(
            path = fillPath,
            brush = Brush.verticalGradient(
                colors = gradientColors,
                startY = 0f,
                endY = graphHeight
            )
        )

        // Draw line
        drawPath(
            path = path,
            color = lineColor,
            style = Stroke(width = 3.dp.toPx(), cap = StrokeCap.Round)
        )
        
        // Draw Dots and Labels
        points.forEachIndexed { index, point ->
            // Outer Dot (White)
            drawCircle(
                color = Color.White,
                radius = 5.dp.toPx(),
                center = androidx.compose.ui.geometry.Offset(point.first, point.second)
            )
            // Inner Dot (Blue border & center)
            drawCircle(
                color = lineColor,
                radius = 5.dp.toPx(),
                center = androidx.compose.ui.geometry.Offset(point.first, point.second),
                style = Stroke(width = 2.dp.toPx())
            )
            drawCircle(
                color = lineColor,
                radius = 2.5.dp.toPx(),
                center = androidx.compose.ui.geometry.Offset(point.first, point.second)
            )

            // Draw Label below the point
            val label = labels.getOrNull(index) ?: ""
            val textLayoutResult = textMeasurer.measure(
                text = label,
                style = TextStyle(color = Color(0xFF64748B), fontSize = 11.sp, fontWeight = FontWeight.Bold)
            )
            
            drawText(
                textLayoutResult = textLayoutResult,
                topLeft = androidx.compose.ui.geometry.Offset(
                    x = (point.first - textLayoutResult.size.width / 2).coerceIn(0f, width - textLayoutResult.size.width),
                    y = graphHeight + 6.dp.toPx()
                )
            )
        }
    }
}

@Composable
fun ModuleShortcutsSection(onNavigate: (String) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Default.Apps, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp))
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "Hızlı Modül Erişimi",
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = Color(0xFF0F172A)
            )
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            ModulePill(
                icon = Icons.Default.CalendarMonth,
                title = "Ön Büro Matrisi",
                color = Color(0xFF2563EB),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("NewReservationCalendarScreen") }
            )
            ModulePill(
                icon = Icons.Default.AccountBalanceWallet,
                title = "Kasa & Finans",
                color = Color(0xFF059669),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("CashRegisterScreen") }
            )
            ModulePill(
                icon = Icons.Default.CleaningServices,
                title = "Kat Hizmetleri",
                color = Color(0xFF7C3AED),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("HousekeepingScreen") }
            )
            ModulePill(
                icon = Icons.Default.RestaurantMenu,
                title = "Restoran POS",
                color = Color(0xFFEA580C),
                modifier = Modifier.weight(1f),
                onClick = { onNavigate("RestaurantPosScreen") }
            )
        }
    }
}

@Composable
fun ModulePill(
    icon: ImageVector,
    title: String,
    color: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = modifier.clickable { onClick() }
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .clip(RoundedCornerShape(6.dp))
                    .background(color.copy(alpha = 0.1f)),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = title, tint = color, modifier = Modifier.size(16.dp))
            }
            Text(
                text = title,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                color = Color(0xFF1E293B),
                maxLines = 1
            )
        }
    }
}
